import React, { useEffect, useState } from "react";
import { AppBar, Box, Collapse, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Tooltip, Typography, useMediaQuery, } from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import StoreIcon from "@mui/icons-material/Store";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import TimelineIcon from "@mui/icons-material/Timeline";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MapIcon from "@mui/icons-material/Map";
import LogoutIcon from "@mui/icons-material/Logout";
import LanguageIcon from "@mui/icons-material/Language";
import { useTranslation } from "react-i18next";
import { Flags } from "../components/Flags";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

const drawerWidth = 240;


const getMenuGroups = (t) => [
    {
        title: t("menu.overview"),
        key: "overview",
        roles: ["ADMIN", "RESELLER", "CUSTOMER"],
        items: [
            { to: "/app/dashboard", label: t("menu.dashboard"), icon: <DashboardIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
            { to: "/app/map", label: t("menu.map"), icon: <MapIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
            { to: "/app/alarm", label: t("menu.alarm"), icon: <NotificationsActiveIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
        ],
    },
    {
        title: t("menu.admin"),
        key: "admin",
        roles: ["ADMIN"],
        items: [
            { to: "/app/users", label: t("menu.users"), icon: <SupervisorAccountIcon />, roles: ["ADMIN"] },
        ],
    },
    {
        title: t("menu.resellers"),
        key: "resellers",
        roles: ["ADMIN", "RESELLER"],
        items: [
            { to: "/app/reseller", label: t("menu.resellers"), icon: <StoreIcon />, roles: ["ADMIN"] },
            { to: "/app/resellerusers", label: t("menu.resellerUsers"), icon: <PeopleIcon />, roles: ["ADMIN", "RESELLER"] },
        ],
    },
    {
        title: t("menu.customers"),
        key: "customers",
        roles: ["ADMIN", "RESELLER", "CUSTOMER"],
        items: [
            { to: "/app/customer", label: t("menu.customers"), icon: <BusinessIcon />, roles: ["ADMIN", "RESELLER"] },
            { to: "/app/customerusers", label: t("menu.customerUsers"), icon: <PeopleIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
            { to: "/app/locations", label: t("menu.locations"), icon: <LocationOnIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
        ],
    },
    {
        title: t("menu.makmini"),
        key: "makmini",
        roles: ["ADMIN", "RESELLER", "CUSTOMER"],
        items: [
            { to: "/app/makmini", label: t("menu.makminiDevices"), icon: <WaterDropIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
            { to: "/app/makminitelemetry", label: t("menu.makminiTelemetry"), icon: <DataUsageIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
            { to: "/app/makministatistics", label: t("menu.makminiStatistics"), icon: <BarChartIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
        ],
    },
    {
        title: t("menu.akiferra"),
        key: "akiferra",
        roles: ["ADMIN", "RESELLER", "CUSTOMER"],
        items: [
            { to: "/app/akiferra", label: t("menu.akiferraDevices"), icon: <WaterDropIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
            { to: "/app/akiferratelemetry", label: t("menu.akiferraTelemetry"), icon: <DataUsageIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
            { to: "/app/akiferrastatistics", label: t("menu.akiferraStatistics"), icon: <BarChartIcon />, roles: ["ADMIN", "RESELLER", "CUSTOMER"] },
        ],
    },
];

const SidebarLayout = () => {
    const { t, i18n } = useTranslation();
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const firstName = localStorage.getItem("firstName") || sessionStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || sessionStorage.getItem("lastName") || "";
    const resellerName = localStorage.getItem("resellerName") || sessionStorage.getItem("resellerName") || "";
    const customerName = localStorage.getItem("customerName") || sessionStorage.getItem("customerName") || "";

    let displayName = `${firstName} ${lastName}`.trim() || t("common.user");

    if (role === "RESELLER" && resellerName) {
        displayName += ` - ${resellerName}`;
    } else if (role === "CUSTOMER") {
        const parts = [];
        if (customerName) parts.push(customerName);
        if (resellerName) parts.push(`(${resellerName})`);
        if (parts.length) displayName += ` - ${parts.join(" ")}`;
    }


    const [open, setOpen] = useState(true);
    const [openGroup, setOpenGroup] = useState(null);
    const [langAnchor, setLangAnchor] = useState(null);

    const handleLangMenuOpen = (e) => setLangAnchor(e.currentTarget);
    const handleLangMenuClose = () => setLangAnchor(null);
    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem("i18nextLng", lang);
        handleLangMenuClose();
    };

    const location = useLocation();
    const navigate = useNavigate();
    const isDesktop = useMediaQuery("(min-width:600px)");

    const handleLogout = () => {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/");
    };

    const toggleGroup = (key) => {
        setOpenGroup((prevKey) => (prevKey === key ? null : key));
    };

    const hasAccess = (roles) => {
        if (!roles || roles.length === 0) return true;
        return roles.includes(role);
    };

    const menuGroups = getMenuGroups(t).filter((g) => hasAccess(g.roles));

    useEffect(() => {
        const activeGroup = menuGroups.find((group) =>
            group.items.some(
                (item) => {
                    const current = location.pathname;
                    const pathMatch =
                        current === item.to || current.startsWith(item.to + "/");
                    return pathMatch && hasAccess(item.roles);
                }
            )
        );

        if (activeGroup) {
            setOpenGroup(activeGroup.key);
        }
    }, [location.pathname]);


    return (
        <Box sx={{ display: "flex" }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    bgcolor: "primary.main",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton onClick={() => setOpen(!open)} color="inherit">
                            {open ? <ChevronLeftIcon /> : <MenuIcon />}
                        </IconButton>
                        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: "0.5px" }}>
                            Maksmart CRM Panel
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Tooltip title={t("common.changeLanguage")}>
                            <IconButton
                                onClick={handleLangMenuOpen}
                                color="inherit"
                                sx={{
                                    p: 0.5,
                                    border: "1px solid rgba(255, 255, 255, 0.3)",
                                    width: 40,
                                    height: 40,
                                    bgcolor: "rgba(255, 255, 255, 0.1)",
                                    backdropFilter: "blur(10px)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        transform: "scale(1.1)",
                                        bgcolor: "rgba(255, 255, 255, 0.2)",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                                    },
                                }}
                            >
                                <LanguageIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={langAnchor}
                            open={Boolean(langAnchor)}
                            onClose={handleLangMenuClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            <MenuItem onClick={() => changeLanguage("tr")}>
                                {Flags.tr}
                                <span style={{ marginLeft: 8 }}>Türkçe</span>
                            </MenuItem>
                            <MenuItem onClick={() => changeLanguage("en")}>
                                {Flags.gb}
                                <span style={{ marginLeft: 8 }}>English</span>
                            </MenuItem>
                        </Menu>

                        <Box sx={{ textAlign: "right", lineHeight: 1.2 }}>
                            <Typography variant="body2" fontWeight="bold">
                                {`${firstName} ${lastName}`.trim() || t("common.user")}
                            </Typography>

                            {role === "ADMIN" && (
                                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}>
                                    {role}
                                </Typography>
                            )}

                            {role === "RESELLER" && (
                                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}>
                                    {resellerName || role}
                                </Typography>
                            )}

                            {role === "CUSTOMER" && (
                                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}>
                                    {customerName ? `${customerName}` : ""}
                                    {resellerName ? ` (${resellerName})` : ""}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            <Drawer
                variant={isDesktop ? "persistent" : "temporary"}
                anchor="left"
                open={open}
                onClose={() => setOpen(false)}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                        transition: "0.3s",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        background: "#FFFFFF",
                        borderRight: "1px solid rgba(0, 0, 0, 0.08)",
                    },
                }}
            >
                <Toolbar />
                <Divider />

                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                    <List>
                        {menuGroups.map((group) => (
                            <Box key={group.key}>
                                <ListItemButton
                                    onClick={() => toggleGroup(group.key)}
                                    sx={{
                                        px: 0,
                                        py: 1.5,
                                        mx: 1,
                                        my: 0.5,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            bgcolor: "rgba(2, 136, 209, 0.04)",
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={group.title}
                                        primaryTypographyProps={{
                                            fontWeight: 600,
                                            fontSize: "0.9rem",
                                            color: "text.primary",
                                        }}
                                    />
                                    {openGroup === group.key ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>

                                <Collapse in={openGroup === group.key} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {group.items
                                            .filter((item) => hasAccess(item.roles))
                                            .map((item) => (
                                                <ListItemButton
                                                    key={item.to}
                                                    component={Link}
                                                    to={item.to}
                                                    selected={location.pathname === item.to}
                                                    sx={{
                                                        pl: 4,
                                                        pr: 0,
                                                        py: 1,
                                                        mx: 1,
                                                        my: 0.25,
                                                        transition: "all 0.2s ease",
                                                        "&.Mui-selected": {
                                                            bgcolor: "rgba(2, 136, 209, 0.08)",
                                                            borderLeft: "3px solid",
                                                            borderColor: "primary.main",
                                                            "& .MuiListItemText-primary": {
                                                                fontWeight: 600,
                                                                color: "primary.main",
                                                            },
                                                            "& .MuiListItemIcon-root": {
                                                                color: "primary.main",
                                                            },
                                                        },
                                                        "&.Mui-selected:hover": {
                                                            bgcolor: "rgba(2, 136, 209, 0.12)",
                                                        },
                                                        "&:hover": {
                                                            bgcolor: "rgba(2, 136, 209, 0.04)",
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText primary={item.label} />
                                                </ListItemButton>
                                            ))}
                                    </List>
                                </Collapse>
                            </Box>
                        ))}
                    </List>
                </Box>

                <Divider />
                <Box sx={{ mt: "auto", display: "flex", justifyContent: "center", py: 1 }}>
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            px: 2,
                            py: 1,
                            transition: "all 0.2s ease",
                            justifyContent: "center",
                            "&:hover": {
                                bgcolor: "rgba(211, 47, 47, 0.08)",
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <LogoutIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                            primary={t("menu.logout")}
                            primaryTypographyProps={{
                                fontWeight: 600,
                            }}
                        />
                    </ListItemButton>
                </Box>
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    mt: 8,

                    ml: isDesktop && open ? `${drawerWidth}px` : 0,
                    width: isDesktop && open ? `calc(100% - ${drawerWidth}px)` : "100%",

                    transition: "margin-left 0.3s ease, width 0.3s ease",
                    overflowX: "hidden"
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
};

export default SidebarLayout;
